
import bus from '@/utils/event_bus'
import PRODUUZIT_FRONT_ENV from "@/utils/defaults";
import { state_init } from "./state_init";
import { useAppStore } from "@/stores/app"
import { useSelectedStore } from "@/stores/selected"
import { useSessionStore } from "@/stores/session"
import { useCompanyStore } from "@/stores/company"
import { useConstructiblesStore } from "@/stores/constructibles"
import { useViewerStore } from "@/stores/viewer"
import { useRecordsStore } from '@/stores/records';
import { useSelectedLogisticsStore } from '@/stores/selected_logistics';


// INTERFACE MODE
const viewerMode = function () {
    const app_store = useAppStore()
    app_store.setViewerMode()
}

const logisticsMode = function () {
    const app_store = useAppStore()
    app_store.setLogisticsMode()
}

const managerMode = function () {
    const app_store = useAppStore()
    app_store.setManagerMode()
}

const deliveryMode = function () {
    const app_store = useAppStore()
    app_store.setDeliveryMode()
}


// VIEWER MODES WHEN INTERFACE IS IN VIEWER MODE
const showConstructibleList = function () {
    const viewer_store = useViewerStore()
    viewer_store.setDesignsMode(false)
}

const showDesignsList = function () {
    const viewer_store = useViewerStore()
    viewer_store.setDesignsMode(true)
}


const onlyManagersGuard = function () {
    const session_store = useSessionStore()
    let auth = session_store.user_object.auth
    if (auth.company_admin || auth.company_manager || auth.is_superuser) {
        return
    }
    bus.emit('notification', { timeout: 3000, text: "You don't have permissions to enter this view. Contact your company admin", color: "error" })
    return false
}

// Allows managers and production workers with logistics tipos
const managersOrLogisticsWorkers = function () {
    const session_store = useSessionStore()
    let auth = session_store.user_object.auth
    if (auth.company_admin || auth.company_manager || auth.is_superuser) {
        return
    }
    if (session_store.iam_prod_worker && session_store.pl_has_logistics_tipos) {
        return
    }
    if (session_store.iam_prod_worker && !session_store.selected_line) {
        return
    }
    bus.emit('notification', { timeout: 3000, text: "You don't have permissions to enter this view. Contact your company admin", color: "error" })
    return false
}

// Managers and production workers. Delivery is shop-floor work, so a worker
// belongs in it — but unlike the logistics viewer it is not narrowed by the
// production line's tipos, because loading a rack is not a station on a line.
// The module flag is checked separately by `hasModuleDelivery`.
const managersOrDeliveryWorkers = function () {
    const session_store = useSessionStore()
    if (session_store.iam_manager_or_admin) return
    if (session_store.iam_prod_worker) return
    bus.emit('notification', { timeout: 3000, text: "You don't have permissions to enter this view. Contact your company admin", color: "error" })
    return false
}

const onlyAdminsGuard = function () {
    const session_store = useSessionStore()
    let auth = session_store.user_object.auth
    if (auth.company_admin || auth.is_superuser) {
        return
    }
    bus.emit('notification', { timeout: 3000, text: "You don't have permissions to enter this view. Contact your company admin", color: "error" })
    return false
}


const hasSessionObjectGuard = async function () {
    const session_store = useSessionStore()
    const company_store = useCompanyStore()
    if (session_store.user_object._flag == "not-initialized") {
        await session_store.getUserObject()
        company_store.loadProjects()
        company_store.loadStandardLibraries()
    }
}

const check_login_subroutine = function (to) {
    const session_store = useSessionStore()
    const is_android = PRODUUZIT_FRONT_ENV.IS_ANDROID
    // Android lock
    if (is_android && !session_store.androidLicensed) {
        bus.emit('notification', { timeout: 3000, text: "Don't have Android license enabled for this user. Contact your company admin.", color: "error" })
        return { name: 'login', query: { redirect: to.fullPath } }
    }
    // Auth lock
    if (session_store.authenticated) {
        return
    }
    return { name: 'login', query: { redirect: to.fullPath } }
}

const isAuthenticated = async function (to) {
    const session_store = useSessionStore()
    if (session_store.user_object._flag == "not-initialized") {
        console.log("[i] Initializing user object.")
        await session_store.getUserObject()
    }
    return check_login_subroutine(to)
}



// Generic module guard factory.
// Pass one or more module flag names; the guard passes if the company has ANY of them enabled.
// Usage in router: requiresModules(['m_logistics'])  or  requiresModules(['m_bom', 'm_machines'])
const requiresModules = (modules) => function () {
    const session_store = useSessionStore()
    if (session_store.hasAnyModule(modules)) {
        return
    }
    bus.emit('notification', { timeout: 3000, text: "Your company doesn't have access to this module. Contact your company admin.", color: "warning" })
    return false
}

const hasModuleProduction = requiresModules(['m_core'])
const hasModuleForecast = requiresModules(['m_prod_management'])
const hasModuleLogistics = requiresModules(['m_logistics'])
const hasModuleDelivery = requiresModules(['m_delivery'])
const hasModuleLite = requiresModules(['m_lite'])
const hasModuleMultipanel = requiresModules(['m_multipanel'])

// Redirect production workers with only logistics tipos to the logistics viewer
const prodWorkerLogisticsRedirect = function () {
    const session_store = useSessionStore()
    const selected_store = useSelectedStore()
    if (session_store.iam_prod_worker && session_store.pl_only_logistics_tipos) {
        const project_uuid = selected_store.selected_project?.uuid
        if (project_uuid && project_uuid !== 'all') {
            return { name: 'logistics', params: { project_uuid } }
        }
        return { name: 'company_logistics' }
    }
}

// Going to the login page is never leaving work behind: it happens when the
// session is gone, and then nothing in the view can be saved anyway. Holding
// it left a logged-out tablet on the viewer, where every request is aborted.
const isGoingToLogin = (to) => to.name === "login"

const prevConstHasRCPRunning = function (to, from) {
    if (isGoingToLogin(to)) return true
    if (from.meta?.dont_leave_if_rcp_running && !to.query?.bypass_rcp_check) {
        const records_store = useRecordsStore()
        // const session_store = useSessionStore()

        if (records_store.rcpIsRunning && !records_store.rcpIsReady) {  // && session_store.force_checklist_start  ????????????
            console.log("[v] RCP is running. Can't leave this view.")
            console.log(records_store.rcp)
            // this to be catched by component and eventualy redirected
            bus.emit("events/viewer/launch_rcp_running_dialog", to)
            return false
        }
    }
    return true
}


// Same idea as prevConstHasRCPRunning, for the logistics viewer: a started
// part batch is work in progress, and walking away from it silently loses the
// only record of when it stopped.
const prevPartBatchRunning = function (to, from) {
    if (isGoingToLogin(to)) return true
    if (from.meta?.dont_leave_if_batch_running && !to.query?.bypass_batch_check) {
        const slog_store = useSelectedLogisticsStore()
        // Arriving at the batch that is already selected is not leaving it:
        // that is the address catching up with a batch just started from the
        // view itself. A pick of a *different* batch navigates before the store
        // changes, so there the two disagree and the warning still fires.
        if (to.query?.part_batch && to.query.part_batch == slog_store.selected_part_batch) {
            return true
        }
        if (slog_store.batch_running) {
            console.log("[v] Part batch is running. Can't leave this view.")
            bus.emit("events/logistics/launch_batch_running_dialog", to)
            return false
        }
    }
    return true
}


const PROD_USER_VIEWS = [
    "home",
    "login",
    "qr",
    "account",
    "project_viewer",
    "viewer",
    "logistics",
    "company_logistics",
    "ass_viewer",
    "mod_viewer",
]

let IS_THIS_INIT_FLAG = true


// MAIN ASYNC STORE ACTIONS DISPATCHER
// ALL ASYNC LOADERS CAN BE CENTRALIZED HERE IF NEEDED
const asyncStoreDispatcher = async function (to) {
    const app_store = useAppStore()
    // "route" drives the app-wide overlay: it must be popped on every exit
    // path (return, redirect, throw, superseded navigation), so the pop
    // lives in this finally instead of a later guard that may never run.
    app_store.pushLoading("route")
    try {
        return await routeStoreDispatch(to)
    } finally {
        app_store.popLoading("route")
    }
}

const routeStoreDispatch = async function (to) {

    // Get stores
    const selected_store = useSelectedStore()
    const session_store = useSessionStore()
    const app_store = useAppStore()
    const constructibles_store = useConstructiblesStore()
    const viewer_store = useViewerStore()

    // Initial state only on first load
    if (IS_THIS_INIT_FLAG) {
        state_init(to)
        IS_THIS_INIT_FLAG = false
    }


    // Promise list for route
    let promises = []

    // Determine UUIDs & loading flags
    const current_pr = selected_store.selected_project?.uuid
    const following_pr = to.params?.project_uuid
    const load_pr = following_pr && current_pr != following_pr

    const current_mod = selected_store.selected_mod?.uuid
    const following_mod = to.params?.mod_uuid
    const load_mod = following_mod && current_mod != following_mod
    const clean_mod = !following_mod

    const current_ass = selected_store.selected_ass?.uuid
    const following_ass = to.params?.ass_uuid
    const load_ass = following_ass && current_ass != following_ass
    const clean_ass = !following_ass

    const current_modd = selected_store.selected_modd?.uuid
    const following_modd = to.params?.modd_uuid
    const load_modd = following_modd && current_modd != following_modd

    const current_assd = selected_store.selected_assd?.uuid
    const following_assd = to.params?.assd_uuid
    const load_assd = following_assd && current_assd != following_assd

    const current_group = selected_store.selected_multipanel_group?.group?.uuid
    const following_group = to.params?.group_uuid
    const load_group = following_group && current_group != following_group
    const clean_group = !following_group



    // Project loader
    // This only works if the project changes actually in route
    if (load_pr) {
        app_store.pushLoading("project")

        console.log("[v] Selecting project in route watcher. Will launch store selector")


        promises.push(selected_store.loadProject(following_pr))
        promises.push(constructibles_store.loadMyFirstConstructible(following_pr))

    }

    // Set viewer mode
    if (following_mod || following_modd) viewer_store.setLevel("module")
    if (following_ass || following_assd) viewer_store.setLevel("assembly")
    if (following_group) viewer_store.setLevel("multipanel_group")

    // ALWAYS remove designs mode for prod workers
    if (session_store.iam_prod_worker) viewer_store.setDesignsMode(false)

    // Load selections for viewer
    if (load_mod) {
        app_store.pushLoading("constructible_" + following_mod)
        console.log("[v] Loading module through route watcher.")
        promises.push(selected_store.loadModule(following_mod))
    }
    if (clean_mod) {
        selected_store.setModule(null)
    }
    if (load_ass) {
        app_store.pushLoading("constructible_" + following_ass)
        console.log("[v] Loading assembly through route watcher.")
        promises.push(selected_store.loadAssembly(following_ass))
    }
    if (clean_ass) {
        selected_store.setAssembly(null)
    }
    if (load_modd) {
        app_store.pushLoading("constructible_" + following_modd)
        console.log("[v] Loading module design through route watcher.")
        promises.push(selected_store.loadModuleDesign(following_modd))
    }
    if (load_assd) {
        app_store.pushLoading("constructible_" + following_assd)
        console.log("[v] Loading assembly design through route watcher.")
        promises.push(selected_store.loadAssemblyDesign(following_assd))
    }
    if (load_group) {
        console.log("[v] Loading multipanel group through route watcher.")
        promises.push(selected_store.loadMultipanelGroup(following_group))
    }
    if (clean_group) {
        selected_store.clearMultipanelGroup()
    }


    // IMPORTANTE. SI ESTA EN EL VIEWER Y ES PROD USER, AL CARGAR PRIMER PANEL
    // SE TIENE QUE REDIRIGIR AL USER AL PRIMER PANEL (O MODULO)


    try {
        await Promise.all(promises)
        // redirect to first constructible if prod user changes project
        if (load_pr && session_store.iam_prod_worker) {
            const first = constructibles_store.myFirst()
            if (first) {
                const route_name = first.level === 'assembly' ? 'ass_viewer' : 'mod_viewer'
                const params = first.level === 'assembly'
                    ? { ass_uuid: first.uuid }
                    : { mod_uuid: first.uuid }
                return { name: route_name, params }
            }
            return { name: "viewer" }
        }
    } finally {
        app_store.popLoading("project")
        app_store.popLoadingIncludes("constructible")
    }
}


const beforeEachGuard = function (to) {
    const app_store = useAppStore()
    app_store.setRouteName(to.name)
    document.title = `produuz.it · ${to.name}`
}

export {
    viewerMode,
    managerMode,
    logisticsMode,
    deliveryMode,
    showConstructibleList,
    showDesignsList,
    onlyManagersGuard,
    managersOrLogisticsWorkers,
    managersOrDeliveryWorkers,
    onlyAdminsGuard,
    hasSessionObjectGuard,
    isAuthenticated,
    requiresModules,
    hasModuleProduction,
    hasModuleForecast,
    hasModuleLogistics,
    hasModuleDelivery,
    hasModuleLite,
    hasModuleMultipanel,
    prodWorkerLogisticsRedirect,
    prevConstHasRCPRunning,
    prevPartBatchRunning,
    asyncStoreDispatcher,
    beforeEachGuard,
}
